// Copyright (c) 2025 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import {
  AccessTimeOutlined,
  CalendarMonthOutlined,
  DateRangeOutlined,
} from '@/constants/hardfin-standard-icons';
import { ItemFieldType } from '@/items/constants/item-field-types';
import {
  Box,
  Paper,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  styled,
  toggleButtonClasses,
} from '@mui/material';
import { ReactNode } from 'react';

/**
 * Styled toggle button group for field format selection.
 */
const FormatToggleButtonGroup = styled(ToggleButtonGroup)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr 1fr',
  gap: theme.spacing(1),
  width: '100%',
  [`& .${toggleButtonClasses.root}`]: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(2),
    textTransform: 'none',
    flexDirection: 'column',
    alignItems: 'flex-start',
    transition: theme.transitions.create(['border-color', 'background-color']),
  },
  [`& .${toggleButtonClasses.selected}`]: {
    borderColor: theme.palette.primary.main,
    backgroundColor: `${theme.palette.primary.main}08`,
    '&:hover': {
      backgroundColor: `${theme.palette.primary.main}12`,
    },
  },
}));

interface FormatOption {
  value: ItemFieldType;
  label: string;
  description: string;
  icon: ReactNode;
  example: ReactNode;
}

const DATE_TIME_FORMAT_OPTIONS: FormatOption[] = [
  {
    value: 'date',
    label: 'Date',
    description: 'Select date values without time',
    icon: <CalendarMonthOutlined color="primary" />,
    example: (
      <Paper
        variant="outlined"
        sx={{
          width: '100%',
          p: 1,
          mt: 1,
          height: 32,
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <Typography variant="body2" color="text.secondary">
          2023-05-15
        </Typography>
      </Paper>
    ),
  },
  {
    value: 'time',
    label: 'Time',
    description: 'Select time values without date',
    icon: <AccessTimeOutlined color="primary" />,
    example: (
      <Paper
        variant="outlined"
        sx={{
          width: '100%',
          p: 1,
          mt: 1,
          height: 32,
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <Typography variant="body2" color="text.secondary">
          02:30 PM
        </Typography>
      </Paper>
    ),
  },
  {
    value: 'date_time',
    label: 'Date and time',
    description: 'Select both date and time together',
    icon: <DateRangeOutlined color="primary" />,
    example: (
      <Paper
        variant="outlined"
        sx={{
          width: '100%',
          paddingLeft: 0.5,
          mt: 1,
          height: 32,
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <Typography variant="body2" color="text.secondary">
          2023-05-15 02:30 PM
        </Typography>
      </Paper>
    ),
  },
];

interface Props {
  value: ItemFieldType;
  onChange: (value: ItemFieldType) => void;
}

/**
 * A component that allows users to select between different date/time format options.
 */
export function ItemFieldDateTimeFormatSelector(props: Props) {
  const handleChange = (_: unknown, value: ItemFieldType | null) => {
    if (value === null) {
      return;
    }
    props.onChange(value);
  };

  return (
    <FormatToggleButtonGroup
      exclusive
      value={props.value}
      onChange={handleChange}
    >
      {DATE_TIME_FORMAT_OPTIONS.map((option) => (
        <ToggleButton key={option.value} value={option.value}>
          <Stack spacing={1} alignItems="flex-start" width="100%" height="100%">
            <Stack spacing={1} width="100%">
              <Box display="flex" alignItems="center" gap={1}>
                {option.icon}
                <Typography variant="subtitle1">{option.label}</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                {option.description}
              </Typography>
            </Stack>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              width="100%"
              flex={1}
              minHeight={80}
            >
              {option.example}
            </Box>
          </Stack>
        </ToggleButton>
      ))}
    </FormatToggleButtonGroup>
  );
}
